import { Button, Input, notification } from "antd";
import { EditOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ThreadList } from "../components/ThreadList";
import { ThreadDetail } from "../components/ThreadDetail";
import { TierFilterChips } from "../components/TierFilterChips";
import { useCompose } from "../components/ComposeContext";
import { useIsMobile } from "../hooks/useBreakpoint";
import { useWs } from "../hooks/useWs";
import { listThreads, triggerSync } from "../api/threads";
import type { ThreadSummary } from "../api/threads";
import { playChime } from "../utils/sound";

type TierFilter = "all" | "important" | "normal" | "unimportant" | "unknown";

const TIERS: TierFilter[] = ["all", "important", "normal", "unimportant", "unknown"];

function parseTier(raw: string | null): TierFilter {
  return TIERS.includes(raw as TierFilter) ? (raw as TierFilter) : "all";
}

export function InboxPage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const tier = parseTier(searchParams.get("tier"));
  const setTier = (next: TierFilter) => {
    setSearchParams(
      (prev) => {
        const np = new URLSearchParams(prev);
        if (next === "all") np.delete("tier");
        else np.set("tier", next);
        return np;
      },
      { replace: true },
    );
  };
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const initialThreadParam = searchParams.get("thread");
  const [selectedId, setSelectedIdState] = useState<string | null>(initialThreadParam);
  const setSelectedId = useCallback(
    (next: string | null) => {
      setSelectedIdState(next);
      setSearchParams(
        (prev) => {
          const np = new URLSearchParams(prev);
          if (next) np.set("thread", next);
          else np.delete("thread");
          return np;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const fromUrl = searchParams.get("thread");
    if (fromUrl !== selectedId) {
      setSelectedIdState(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [api, ctx] = notification.useNotification();
  const { openCompose } = useCompose();
  const initialSynced = useRef(false);
  const queryRef = useRef({ tier, q });
  queryRef.current = { tier, q };

  const refresh = useCallback(
    async (currentTier: TierFilter, currentQ: string) => {
      setLoading(true);
      try {
        const { threads, nextCursor } = await listThreads({
          tier: currentTier,
          q: currentQ || undefined,
        });
        setThreads(threads);
        setNextCursor(nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const result = await listThreads({
        tier: queryRef.current.tier,
        q: queryRef.current.q || undefined,
        cursor: nextCursor,
      });
      setThreads((prev) => [...prev, ...result.threads]);
      setNextCursor(result.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor]);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      void refresh(tier, q);
    }, 200);
    return () => clearTimeout(handle);
  }, [tier, q, refresh]);

  useEffect(() => {
    if (initialSynced.current) return;
    initialSynced.current = true;
    void triggerSync()
      .catch(() => null)
      .finally(() => {
        void refresh(queryRef.current.tier, queryRef.current.q);
      });
  }, [refresh]);

  useWs((ev) => {
    if (ev.type === "thread:updated") {
      void refresh(queryRef.current.tier, queryRef.current.q);
    }
    if (ev.type === "message:new" && ev.tier === "important") {
      playChime();
      api.open({
        message: `★ ${ev.from}`,
        description: ev.subject || "(无主题)",
        placement: isMobile ? "top" : "topRight",
        duration: 6,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setSelectedId(ev.threadId);
              api.destroy();
            }}
          >
            查看
          </Button>
        ),
      });
    }
  });

  const onSync = async () => {
    await triggerSync().catch(() => null);
    await refresh(tier, q);
  };

  const onAfterAction = async () => {
    await refresh(tier, q);
  };

  const composeBtn = (
    <Button
      type="primary"
      icon={<EditOutlined />}
      onClick={() => openCompose()}
      style={{ height: 40, fontWeight: 500 }}
    >
      写邮件
    </Button>
  );

  const filterBar = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "12px 16px",
        borderBottom: "1px solid var(--ant-color-border)",
        background: "var(--ant-color-bg-container)",
      }}
    >
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="搜索邮件主题、内容或发件人"
        prefix={<SearchOutlined style={{ color: "var(--ant-color-text-secondary)" }} />}
        allowClear
      />
      <TierFilterChips<TierFilter>
        value={tier}
        onChange={setTier}
        options={[
          { label: "全部", value: "all" },
          { label: "重要", value: "important" },
          { label: "普通", value: "normal" },
          { label: "不重要", value: "unimportant" },
          { label: "未分类", value: "unknown" },
        ]}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button icon={<ReloadOutlined />} onClick={onSync} aria-label="刷新" />
        {composeBtn}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {ctx}
        {selectedId ? (
          <ThreadDetail
            threadId={selectedId}
            showBack
            onClose={() => setSelectedId(null)}
            onAfterSend={() => void refresh(tier, q)}
            onAfterAction={async () => {
              await onAfterAction();
              setSelectedId(null);
            }}
          />
        ) : (
          <>
            {filterBar}
            <ThreadList
              threads={threads}
              selectedId={selectedId}
              loading={loading}
              hasMore={Boolean(nextCursor)}
              loadingMore={loadingMore}
              onSelect={setSelectedId}
              onLoadMore={loadMore}
            />
          </>
        )}
      </>
    );
  }

  return (
    <>
      {ctx}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 380px) minmax(0, 1fr)",
          height: "100dvh",
          maxHeight: "100dvh",
          overflow: "hidden",
          background: "var(--ant-color-bg-layout)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
            borderRight: "1px solid var(--ant-color-border)",
            background: "var(--ant-color-bg-container)",
          }}
        >
          {filterBar}
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <ThreadList
              threads={threads}
              selectedId={selectedId}
              loading={loading}
              hasMore={Boolean(nextCursor)}
              loadingMore={loadingMore}
              onSelect={setSelectedId}
              onLoadMore={loadMore}
            />
          </div>
        </div>
        <div style={{ overflow: "hidden", height: "100%", minHeight: 0 }}>
          <ThreadDetail
            threadId={selectedId}
            onAfterSend={() => void refresh(tier, q)}
            onAfterAction={async () => {
              await onAfterAction();
              setSelectedId(null);
            }}
          />
        </div>
      </div>
    </>
  );
}
